import { API_CALL, API_CALL_FAILURE, API_CALL_SUCCESS, SET_ACCOMMODATION_V2_SESSIONS } from 'actions/actionConstants'
import { CHECKOUT_ACCOMMODATION_V2_ENQUIRY } from 'actions/apiActionConstants'
import type { AppAction, DefinedAppAction } from 'actions/types/ActionTypes'
import type { ThunkAction } from 'redux-thunk'
import { checkoutEnquiry, createEnquirySession } from 'api/accommodationBooking'
import { removeItems, updateCheckoutItem } from 'actions/CheckoutActions'
import { buildEnquirySessionPayload } from 'lib/accommodation/buildEnquirySessionPayload'
import {
  applyAccommodationV2ReplacementSession,
  buildReplacementEnquirySessionPayload,
} from 'lib/accommodation/buildReplacementEnquirySessionPayload'
import { hasAccommodationV2SessionError, isAccommodationV2PurchaseBlocked } from 'checkout/lib/utils/accommodationV2/outcomes'
import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import { getFormattedCartItems } from 'checkout/selectors/payment/orderCreationSelectors'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { getIsLuxPlusLPPEnabled, getLppMemberPricingEnabledForCheckout } from 'luxPlus/selectors/featureToggle'
import type { AppDispatch } from 'src/client/store'
import submitLoyaltyPointsEarnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsEarnCalculationRequest'
import submitLoyaltyPointsBurnCalculationRequests from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequests'
import getCheckoutPointsCalculationRequests from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutPointsCalculationRequests'
import getCheckoutLoyaltyPointsBurnCalculationRequests from 'checkout/selectors/loyalty/getCheckoutLoyaltyPointsBurnCalculationRequests'
import getLoyaltyPointBurnCalculationKey from 'checkout/lib/utils/loyalty/getLoyaltyPointBurnCalculationKey'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getOrderLoyalty from 'loyalty/selectors/checkout/order/getOrderLoyalty'
import { getAgentHubCommissionMap } from 'agentHub/selectors/agentHubSelectors'
import { getCalculationRequestAfterCommissionIfAgentHubEnabled } from 'lib/agentHub/agentHubUtils'
import wait from 'lib/promise/wait'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import { isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/isLuxLoyaltyEnabledInAgentHub'
import { trackCheckoutObservabilityEvent } from 'analytics/snowplow/checkoutAnalytics'

const LOYALTY_CALCULATION_POLL_MS = 50
const LOYALTY_CALCULATION_MAX_WAIT_MS = 5000

export interface AccommodationV2PurchasePreparation {
  blocked: boolean
  formattedCartItems: ReturnType<typeof getFormattedCartItems>
  payableNow: number
  loyalty: ReturnType<typeof getOrderLoyalty>
}

export function setAccommodationV2Sessions(
  sessions: Array<App.AccommodationEnquirySession>,
): AppAction {
  return {
    type: SET_ACCOMMODATION_V2_SESSIONS,
    data: sessions,
  }
}

export interface InitAccommodationV2SessionSelection {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>>
}

export interface InitAccommodationV2SessionsParams {
  /** One selection per cart item — rates may differ across multi-room individual mode. */
  selections: Array<InitAccommodationV2SessionSelection>
  checkIn: string
  checkOut: string
  region: string
  /** Shopped LuxPlus+ eligibility stamped onto each session for checkout enquiry. */
  isLuxPlusUser?: boolean
}

/**
 * Creates one enquiry session per selection in parallel.
 *
 * Fail-closed for booking: any create failure rejects the whole attempt so callers
 * abort to sold-out rather than proceeding with a partial set of session ids.
 * Successful creates from a partial failure are still dispatched (so Redux tracks
 * them); the FE has no delete API yet, so those orphans expire via server TTL.
 * Returns session ids in selection order.
 */
export async function initAccommodationV2Sessions(
  dispatch: AppDispatch,
  params: InitAccommodationV2SessionsParams,
): Promise<Array<string>> {
  const state = dispatch((_action: any, getState: () => App.State) => getState())
  // Always a new booking. Checkout has not initialised yet, so `existingOrder` may be stale.
  const lppMemberPricingEnabled = getIsLuxPlusLPPEnabled(state)
  const settled = await Promise.allSettled(params.selections.map(({ rate, occupancy, selectedOptionalBundles }) =>
    createEnquirySession(buildEnquirySessionPayload({
      rate,
      occupancy,
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      region: params.region,
      selectedOptionalBundles,
      ...(params.isLuxPlusUser && { isLuxPlusUser: true }),
      lppMemberPricingEnabled,
    })),
  ))

  const createdSessions = settled.flatMap(result => (
    result.status === 'fulfilled' ? [result.value] : []
  ))
  if (createdSessions.length > 0) {
    dispatch(setAccommodationV2Sessions(createdSessions))
  }

  const firstRejection = settled.find(result => result.status === 'rejected')
  if (firstRejection?.status === 'rejected') {
    throw firstRejection.reason
  }

  return createdSessions.map(session => session.id)
}

export function refreshAccommodationV2Sessions(sessionIds: Array<string>): AppAction {
  return (dispatch, getState) => {
    if (sessionIds.length === 0) return

    dispatch({
      type: API_CALL,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      request: () => checkoutEnquiry({
        items: sessionIds.map(sessionId => ({ sessionId })),
        transactionKey: getState().checkout.cart.transactionKey,
      }),
    })
  }
}

async function dispatchCheckoutEnquiry(
  dispatch: AppDispatch,
  getState: () => App.State,
  sessionIds: Array<string>,
): Promise<void> {
  if (sessionIds.length === 0) return

  try {
    const data = await checkoutEnquiry({
      items: sessionIds.map(sessionId => ({ sessionId })),
      transactionKey: getState().checkout.cart.transactionKey,
    })
    dispatch({
      type: API_CALL_SUCCESS,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      data,
    })
  } catch(error) {
    dispatch({
      type: API_CALL_FAILURE,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      error,
    })
  }
}

/**
 * Loyalty: each checkout enquiry returns a newly encrypted `mx`, even when the price is unchanged,
 * and `mx` is part of the earn and burn calculation keys. Fetch the new keys and wait for them, so
 * the order isn't built while they are in flight (it would send no pending points or points payment).
 */
async function settleCheckoutLoyaltyCalculations(
  dispatch: AppDispatch,
  getState: () => App.State,
): Promise<void> {
  const state = getState()
  const customerId = state.auth.account.memberId ?? ''
  const agentHubCommissionMap = getAgentHubCommissionMap(state)
  const earnRequests = getCheckoutPointsCalculationRequests(state)
    .map(request => getCalculationRequestAfterCommissionIfAgentHubEnabled(request, agentHubCommissionMap))
  const burnRequests = getCheckoutLoyaltyPointsBurnCalculationRequests(state)

  earnRequests.forEach(request => dispatch(submitLoyaltyPointsEarnCalculationRequest(request)))
  if (getIsLoyaltyEnabled(state)) {
    dispatch(submitLoyaltyPointsBurnCalculationRequests(burnRequests))
  }

  const earnKeys = earnRequests.map(request => buildPointsKey(request, customerId))
  const burnKey = burnRequests.length ? getLoyaltyPointBurnCalculationKey(burnRequests) : undefined
  // Requests the actions skip (e.g. loyalty disabled) never get a store entry, so only wait on fetching ones
  const isSettled = () => {
    const { pointsEarnCalculations, pointsBurnCalculations } = getState().loyalty
    return earnKeys.every(key => !pointsEarnCalculations[key]?.fetching) &&
      (!burnKey || !pointsBurnCalculations[burnKey]?.fetching)
  }

  const deadline = Date.now() + LOYALTY_CALCULATION_MAX_WAIT_MS
  while (!isSettled() && Date.now() < deadline) {
    await wait(LOYALTY_CALCULATION_POLL_MS)
  }
}

export function resolveCheckoutLoyaltyBeforePurchase(): ThunkAction<
  Promise<App.CheckoutOrderLoyalty | undefined>,
  App.State,
  undefined,
  DefinedAppAction
> {
  return async(dispatch, getState) => {
    const initial = getOrderLoyalty(getState())
    if (initial || !getIsLoyaltyEnabled(getState()) || isLuxLoyaltyEnabledInAgentHub) {
      return initial
    }

    const accountRefreshSucceeded = await dispatch(fetchLoyaltyAccount(undefined, { force: true }))

    const state = getState()
    if (accountRefreshSucceeded === false) {
      trackCheckoutObservabilityEvent({
        subject: 'order',
        action: 'degraded',
        type: 'operational',
        module: 'loyalty',
        scope: 'cart',
        reasonCode: 'network_error',
        isBlocking: false,
        canContinue: true,
      })
      return undefined
    }

    if (!getHasLoyaltyAccount(state) || !getIsCurrentRegionUserLoyaltyRegion(state)) {
      return undefined
    }

    // enquireAccommodationV2BeforePurchase settles earn/burn with post-enquiry mx keys
    if (state.checkout.cart.items.some(isAccommodationV2Item)) {
      return getOrderLoyalty(getState())
    }

    await settleCheckoutLoyaltyCalculations(dispatch, getState)
    return getOrderLoyalty(getState())
  }
}

/**
 * Re-enquires every v2 session on the cart immediately before purchase.
 * Checkout enquiry rematerialises the bedbank supplier session; a stale one
 * fails booking create as `supplier_session_failed`.
 */
export function enquireAccommodationV2BeforePurchase(): ThunkAction<
  Promise<AccommodationV2PurchasePreparation>,
  App.State,
  undefined,
  DefinedAppAction
> {
  return async(dispatch, getState) => {
    const sessionIds = getState().checkout.cart.items
      .filter(isAccommodationV2Item)
      .flatMap(item => item.sessionId ? [item.sessionId] : [])

    await dispatchCheckoutEnquiry(dispatch, getState, sessionIds)

    const { cart, accommodationSessions } = getState().checkout
    const blocked = isAccommodationV2PurchaseBlocked(cart.items, accommodationSessions)
    if (!blocked) {
      await settleCheckoutLoyaltyCalculations(dispatch, getState)
    }

    const state = getState()
    return {
      blocked,
      formattedCartItems: getFormattedCartItems(state),
      payableNow: getCheckoutTotalsView(state).data.payableNow,
      loyalty: getOrderLoyalty(state),
    }
  }
}

/**
 * Accepts a mixed checkout-enquiry result: mint replacement sessions for `price_changed`
 * rooms, drop `unavailable` rooms, then re-enquire the survivors.
 */
export function acceptAccommodationV2EnquiryOutcomes(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const sessions = state.checkout.accommodationSessions
    const items = state.checkout.cart.items.filter(isAccommodationV2Item)
    const isLuxPlusUser = !!getEnquirySubscriber(state)?.tier
    const lppMemberPricingEnabled = getLppMemberPricingEnabledForCheckout(state)

    const priceChangedItems = items.filter(item => (
      item.sessionId && sessions[item.sessionId]?.outcome === 'price_changed'
    ))
    const unavailableItemIds = new Set(
      items
        .filter(item => item.sessionId && sessions[item.sessionId]?.outcome === 'unavailable')
        .map(item => item.itemId),
    )
    const replacementSessionIds = new Map<string, string>()

    for (const item of priceChangedItems) {
      const record = item.sessionId ? sessions[item.sessionId] : undefined
      if (!record?.data || !record.rate) {
        throw new Error('Cannot accept a price change without a live enquiry rate')
      }

      const session = await createEnquirySession(
        buildReplacementEnquirySessionPayload(record.data, record.rate, isLuxPlusUser, lppMemberPricingEnabled),
      )
      replacementSessionIds.set(item.itemId, session.id)
      dispatch(setAccommodationV2Sessions([session]))
      dispatch(updateCheckoutItem(applyAccommodationV2ReplacementSession(item, record.rate, session.id)))
    }

    if (unavailableItemIds.size > 0) {
      dispatch(removeItems([...unavailableItemIds]))
    }

    const remainingSessionIds = items.flatMap((item) => {
      if (unavailableItemIds.has(item.itemId)) return []
      const replacementId = replacementSessionIds.get(item.itemId)
      if (replacementId) return [replacementId]
      return item.sessionId ? [item.sessionId] : []
    })

    await dispatchCheckoutEnquiry(dispatch, getState, remainingSessionIds)

    const nextState = getState()
    if (hasAccommodationV2SessionError(nextState.checkout.cart.items, nextState.checkout.accommodationSessions)) {
      throw new Error('Accommodation checkout enquiry refresh failed')
    }

    return {
      stillBlocked: isAccommodationV2PurchaseBlocked(
        nextState.checkout.cart.items,
        nextState.checkout.accommodationSessions,
      ),
    }
  }
}
